import { useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Download, Loader2, Mail, Send, X } from 'lucide-react';
import { api, type ReportPreview } from '../lib/api';
import { Button } from './ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Badge } from './ui/Badge';
import { EmailPreviewFrame } from './EmailPreviewFrame';
import { useCsvObjectUrl } from '../lib/csv';
import { formatNumber } from '../lib/format';

interface Props {
  reportId: string;
  reportName: string;
  date: string;
  onClose: () => void;
}

export function SendReportModal({ reportId, reportName, date, onClose }: Props) {
  const queryClient = useQueryClient();

  // Recipients come from the CURRENT config (matches what the runner will use).
  const configs = useQuery({
    queryKey: ['reports', 'configs'],
    queryFn: () => api.listReportConfigs(),
    staleTime: 5 * 60 * 1000,
  });
  const currentRecipients = useMemo(() => {
    const cfg = configs.data?.items.find((c) => c.report_id === reportId);
    return cfg?.recipients ?? [];
  }, [configs.data, reportId]);

  const preview = useMutation({
    mutationFn: () => api.previewReport(reportId, date),
  });

  const send = useMutation({
    mutationFn: () => api.runReportNow(reportId, date),
    onSuccess: () => {
      window.setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: ['reports', 'runs'] });
        void queryClient.invalidateQueries({ queryKey: ['reports', 'summary'] });
      }, 8000);
    },
  });

  const autoRanRef = useRef(false);
  useEffect(() => {
    if (!autoRanRef.current) {
      autoRanRef.current = true;
      preview.mutate();
    }
  }, [preview]);

  const csvUrl = useCsvObjectUrl(preview.data?.csv_base64);

  const recipientCount = currentRecipients.length;
  const previewLoading = preview.isPending;
  const previewError = preview.error;
  const previewData = preview.data;
  const sendDisabled =
    previewLoading || !!previewError || send.isPending || send.isSuccess || recipientCount === 0;

  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-4xl bg-card border-l border-border shadow-xl flex flex-col">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Send className="h-4 w-4 text-muted-foreground" />
              Re-send · {reportName}
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Preview, then confirm to email recipients · target {date}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {previewLoading && (
            <div className="flex items-center gap-3 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              Running query and rendering email…
            </div>
          )}

          {previewError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 text-destructive px-4 py-3 text-sm flex gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Preview failed</p>
                <pre className="text-xs mt-1 font-mono whitespace-pre-wrap">
                  {(previewError as Error).message}
                </pre>
                <p className="text-xs mt-2 opacity-90">
                  Re-send is blocked until the preview succeeds — fix the underlying issue and
                  reopen this dialog.
                </p>
              </div>
            </div>
          )}

          {previewData && (
            <>
              <PreviewStats preview={previewData} csvUrl={csvUrl} />

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    Will be sent to {recipientCount} recipient{recipientCount === 1 ? '' : 's'}
                  </CardTitle>
                  <Badge variant="outline" className="text-[10px]">
                    from current config
                  </Badge>
                </CardHeader>
                <CardContent>
                  {recipientCount === 0 ? (
                    <p className="text-sm text-warning flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      No recipients in the current config — nothing to send.
                    </p>
                  ) : (
                    <ul className="space-y-1">
                      {currentRecipients.map((r) => (
                        <li key={r} className="font-mono text-xs">
                          {r}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <EmailPreviewFrame preview={previewData} heightClass="h-[500px]" />
            </>
          )}

          {send.isError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 text-destructive px-4 py-3 text-sm flex gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Send failed</p>
                <p className="text-xs mt-1 font-mono">
                  {(send.error as Error).message}
                </p>
              </div>
            </div>
          )}

          {send.isSuccess && (
            <div className="rounded-md border border-success/40 bg-success/10 text-success px-4 py-3 text-sm flex gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Send queued</p>
                <p className="text-xs mt-1 opacity-90">
                  The runner will execute the query and email recipients in the background. A new
                  audit row will appear in the runs list within ~30 seconds.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground">
            {previewData && (
              <>
                {previewData.row_count === 0 ? (
                  <span className="text-warning inline-flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Preview returned 0 rows
                  </span>
                ) : (
                  <span>{formatNumber(previewData.row_count)} rows in preview</span>
                )}
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              {send.isSuccess ? 'Close' : 'Cancel'}
            </Button>
            <Button onClick={() => send.mutate()} disabled={sendDisabled}>
              {send.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : send.isSuccess ? (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  Sent
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Send to {recipientCount} recipient{recipientCount === 1 ? '' : 's'}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewStats({
  preview,
  csvUrl,
}: {
  preview: ReportPreview;
  csvUrl: string | null;
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <Stat label="Rows" value={formatNumber(preview.row_count)} emphasize={preview.row_count === 0} />
      <Stat label="Duration" value={`${(preview.duration_ms / 1000).toFixed(1)}s`} />
      <Stat label="Subject" value={preview.subject} truncate />
      <Stat
        label="CSV"
        value={
          csvUrl ? (
            <a
              href={csvUrl}
              download={preview.csv_filename}
              className="inline-flex items-center gap-1.5 text-primary hover:underline text-sm"
            >
              <Download className="h-3.5 w-3.5" />
              Download
            </a>
          ) : (
            '—'
          )
        }
      />
    </div>
  );
}

function Stat({
  label,
  value,
  emphasize,
  truncate,
}: {
  label: string;
  value: React.ReactNode;
  emphasize?: boolean;
  truncate?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={`text-sm font-semibold tabular-nums ${emphasize ? 'text-warning' : ''} ${truncate ? 'truncate' : ''}`}
          title={typeof value === 'string' ? value : undefined}
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
